import React, { useEffect, useMemo, useState } from 'react';
import './HomePage.css';
import { WEB_APP_URL, APPLE_APP_URL } from '../services/constants';
import { navigate } from '../services/navigation';
import { getQrUrl } from '../services/formatters';

const homeMeta = {
  title: 'Talkali | Paid Creator Messaging for Fans and Creators',
  description:
    'Talkali lets fans message creators directly. Creators earn from paid replies, gifts, and private unlocks, while fans are charged only when they get a reply.',
};

const audienceContent = {
  creator: {
    eyebrow: 'For creators',
    title: 'Turn attention into paid conversations',
    text:
      'Talkali helps creators stop answering endless DMs for free. Import your Instagram content, set your reply pricing, chat directly with fans, and earn from replies, gifts, and private unlocks in one place.',
    primaryAction: 'Create account',
    primaryPath: "/#apps-section",
    secondaryAction: 'Log in',
    secondaryPath: '/login',
    proofPoints: [
      { icon: '📥', label: 'Instagram import' },
      { icon: '💬', label: 'Paid replies' },
      { icon: '🎁', label: 'Gifts and unlocks' },
    ],
    stepsTitle: 'How Talkali works for creators',
    stepsText:
      'The creator flow is built for quick setup, direct communication, and clear monetization without the usual platform noise.',
    steps: [
      {
        number: '01',
        title: 'Create your profile fast',
        text: 'Set up your creator account and import Instagram content so your profile already looks active and complete.',
      },
      {
        number: '02',
        title: 'Let fans message you directly',
        text: 'Fans reach you through paid messaging, so your time goes to people who genuinely want your attention.',
      },
      {
        number: '03',
        title: 'Earn from replies, gifts, and unlocks',
        text: 'Reply to messages, receive gifts on content, and set prices for private media to turn engagement into real income.',
      },
    ],
    featureEyebrow: 'Creator tools',
    featureTitle: 'Built for creators who want focus, control, and revenue',
    featureText:
      'Talkali is designed around direct fan access. No chasing comments, no fighting for reach, and no wasted time in crowded inboxes.',
    features: [
      {
        icon: '🔔',
        title: 'Fast message alerts',
        text: 'See new fan messages quickly and reply while interest is still high.',
      },
      {
        icon: '🎁',
        title: 'Gifts on posts',
        text: 'Fans can support your photos and videos with gifts directly on your content.',
      },
      {
        icon: '🔒',
        title: 'Private media unlocks',
        text: 'Set a price before private photos or videos in chat can be viewed.',
      },
      {
        icon: '📊',
        title: 'Earnings visibility',
        text: 'Track activity, conversations, and earnings in a cleaner creator workflow.',
      },
    ],
    mock: {
      handle: '@alexnova',
      subtitle: 'Creator account',
      chipClassName: 'mock-chip paid',
      chipText: 'Paid replies on',
      rows: [
        {
          icon: '📥',
          title: 'Instagram import',
          text: 'Profile ready',
        },
        {
          icon: '🔔',
          title: 'New messages',
          text: '3 unread',
        },
        {
          icon: '💸',
          title: 'Today',
          text: 'Replies + gifts',
        },
      ],
      stats: [
        { icon: '👀', value: '2.1k', label: 'visits' },
        { icon: '💵', value: '$860', label: 'earned' },
        { icon: '✉️', value: '14', label: 'replies' },
      ],
      messages: [
        { type: 'incoming', text: 'Can I get a quick reply?' },
        { type: 'outgoing', text: 'Yes — your message is unlocked.' },
      ],
      composer: 'Reply to messages',
    },
  },
  user: {
    eyebrow: 'For fans',
    title: 'Message creators directly and pay only for real replies',
    text:
      'Talkali gives fans a more direct way to reach creators. Start a conversation, send gifts, and know that message credits are used only when a creator replies. If no reply comes within 48 hours, your message credits are refunded.',
    primaryAction: 'Create account',
    primaryPath: "/#apps-section",
    secondaryAction: 'Log in',
    secondaryPath: '/login',
    proofPoints: [
      { icon: '🪙', label: 'Charged only on reply' },
      { icon: '⏱️', label: '48-hour refund protection' },
      { icon: '🎉', label: '30-credit welcome bonus' },
    ],
    stepsTitle: 'How Talkali works for fans',
    stepsText:
      'The fan experience is simple: pick a creator, send a message, and pay only when the conversation actually moves forward.',
    steps: [
      {
        number: '01',
        title: 'Choose a creator and start chatting',
        text: 'Open a creator profile, send a direct message, and start the conversation in a cleaner one-to-one format.',
      },
      {
        number: '02',
        title: 'Get charged only if they reply',
        text: 'Your message credits are used only when the creator answers, not the moment you send the first message.',
      },
      {
        number: '03',
        title: 'Stay protected if they do not answer',
        text: 'If a creator does not reply within 48 hours, your message credits are automatically refunded to your balance.',
      },
    ],
    featureEyebrow: 'Fan experience',
    featureTitle: 'A simpler way to reach creators you actually care about',
    featureText:
      'Talkali is built around direct access, not endless scrolling. Message creators, support content with gifts, and keep everything in one clear place.',
    features: [
      {
        icon: '💬',
        title: 'Direct creator messaging',
        text: 'Send messages straight to creators instead of hoping they notice a comment or DM elsewhere.',
      },
      {
        icon: '🎁',
        title: 'Support with gifts',
        text: 'Send gifts on posts, photos, and videos you want to support.',
      },
      {
        icon: '🪙',
        title: 'Fair message charging',
        text: 'Know exactly when credits are used and when they are refunded.',
      },
      {
        icon: '📱',
        title: 'One focused inbox',
        text: 'Keep your creator conversations, replies, and activity together in one place.',
      },
    ],
    mock: {
      handle: '@alexnova',
      subtitle: 'Creator profile',
      chipClassName: 'mock-chip guarantee',
      chipText: 'Refund rule: 48h',
      rows: [
        {
          icon: '💬',
          title: 'Direct message',
          text: 'Sent to creator',
        },
        {
          icon: '🎁',
          title: 'Send a gift',
          text: 'Support a post',
        },
        {
          icon: '🪙',
          title: 'Credit rule',
          text: 'Charged on reply',
        },
      ],
      stats: [
        { icon: '🎉', value: '30', label: 'bonus' },
        { icon: '⏱️', value: '48h', label: 'protection' },
        { icon: '📨', value: '3', label: 'replies' },
      ],
      messages: [
        { type: 'outgoing', text: 'Loved your latest post.' },
        { type: 'incoming', text: 'Thanks for the message.' },
      ],
      composer: 'Write a message',
    },
  },
};

const faqItems = [
  {
    question: 'What is Talkali?',
    answer:
      'Talkali is a paid creator messaging platform where fans can message creators directly, and creators can earn from replies, gifts, and private media unlocks.',
  },
  {
    question: 'When are credits charged on Talkali?',
    answer:
      'For direct messages, credits are charged only when a creator replies. You are not charged just for sending the first message.',
  },
  {
    question: 'What happens if a creator does not reply?',
    answer:
      'If a creator does not reply within 48 hours, the message credits used for that conversation are refunded back to your balance.',
  },
  {
    question: 'Why would creators use Talkali?',
    answer:
      'Talkali gives creators a cleaner way to manage fan communication, stop answering messages for free, and earn from direct interaction instead of relying only on views and likes.',
  },
  {
    question: 'Where can I download Talkali?',
    answer:
      'The iPhone app is available now. Android is coming soon. You can also use Talkali on the web at app.talkali.com.',
  },
];

function setMetaTag(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

function setLinkTag(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
}

function setStructuredData(id, data) {
  let script = document.getElementById(id);

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

function HomePage() {
  const [audience, setAudience] = useState('user');
  const current = useMemo(() => audienceContent[audience], [audience]);

  const openDestination = (target) => {
    if (/^https?:\/\//i.test(target)) {
      window.location.href = target;
      return;
    }

    navigate(target);
  };

  const scrollToAppsSection = () => {
    const section = document.getElementById('apps-section');

    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePrimaryAction = () => {
    if (
      current.primaryAction === 'Create account'
    ) {
      scrollToAppsSection();
      return;
    }

    openDestination(current.primaryPath);
  };

  useEffect(() => {
    if (typeof window.scrollTo === 'function') {
      window.scrollTo(0, 0);
    }

    const baseUrl = `${window.location.origin}/`;
    const imageUrl = `${window.location.origin}/home_logo.png`;

    document.title = homeMeta.title;
    setMetaTag('name', 'description', homeMeta.description);
    setMetaTag(
      'property',
      'og:title',
      'Talkali | Paid Creator Messaging for Fans and Creators'
    );
    setMetaTag(
      'property',
      'og:description',
      'Message creators directly, pay only when they reply, and use Talkali for paid replies, gifts, and private media unlocks.'
    );
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:url', baseUrl);
    setMetaTag('property', 'og:image', imageUrl);
    setMetaTag(
      'name',
      'twitter:title',
      'Talkali | Paid Creator Messaging for Fans and Creators'
    );
    setMetaTag(
      'name',
      'twitter:description',
      'Direct creator messaging with paid replies, gifts, and private media unlocks.'
    );
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:image', imageUrl);
    setLinkTag('canonical', baseUrl);

    setStructuredData('talkali-home-structured-data', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          name: 'Talkali',
          url: baseUrl,
          description: homeMeta.description,
        },
        {
          '@type': 'WebApplication',
          name: 'Talkali',
          applicationCategory: 'SocialNetworkingApplication',
          operatingSystem: 'Web',
          url: WEB_APP_URL,
          description:
            'Paid creator messaging platform with direct fan chats, paid replies, gifts, private media unlocks, and reply-based charging.',
        },
      ],
    });
  }, []);

  return (
    <main className="home-page">
      <section className="hero-section">
        <div className="hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">Paid creator messaging</span>

            <h1>Talkali lets fans message creators directly — and creators earn from the conversation.</h1>

            <p className="hero-text">
              Fans get a more direct way to reach creators. Creators get paid for the time and attention they give back. With paid replies, gifts, and private media unlocks, Talkali turns engagement into something more valuable than likes.
            </p>

            <div className="hero-actions">
              <button
                className="primary-button"
                type="button"
                onClick={handlePrimaryAction}
              >
                {current.primaryAction}
              </button>

              <button
                className="secondary-button"
                type="button"
                onClick={() => openDestination(current.secondaryPath)}
              >
                {current.secondaryAction}
              </button>
            </div>

            <p className="hero-summary">
              Available on iPhone and web, with Android coming soon.
            </p>

            <div className="audience-toolbar">
              <span className="audience-label">Choose your experience</span>

              <div className="audience-switch">
                <button
                  className={`audience-switch-button ${audience === 'user' ? 'active' : ''}`}
                  type="button"
                  onClick={() => setAudience('user')}
                >
                  Fans
                </button>

                <button
                  className={`audience-switch-button ${audience === 'creator' ? 'active' : ''}`}
                  type="button"
                  onClick={() => setAudience('creator')}
                >
                  Creators
                </button>
              </div>
            </div>

            <div className="audience-panel">
              <span className="audience-panel-eyebrow">{current.eyebrow}</span>
              <h2>{current.title}</h2>
              <p>{current.text}</p>

              <div className="hero-pills">
                {current.proofPoints.map((pill) => (
                  <div className="info-pill" key={pill.label}>
                    <span className="pill-icon">{pill.icon}</span>
                    {pill.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            className={`hero-visual ${
              audience === 'creator' ? 'creator-visual' : 'user-visual'
            }`}
          >
            <div className="phone-card">
              <div className="phone-notch" />

              <div className="phone-screen">
                <div className="mock-top">
                  <div className="mock-profile">
                    <div className="mock-avatar">👤</div>

                    <div className="mock-lines">
                      <strong>{current.mock.handle}</strong>
                      <span>{current.mock.subtitle}</span>
                    </div>
                  </div>

                  <div className={current.mock.chipClassName}>{current.mock.chipText}</div>
                </div>

                <div className="mock-chat-list">
                  {current.mock.rows.map((row) => (
                    <div className="chat-row" key={row.title}>
                      <div className="chat-icon">{row.icon}</div>

                      <div className="chat-content">
                        <strong>{row.title}</strong>
                        <span>{row.text}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mock-stat-row">
                  {current.mock.stats.map((stat) => (
                    <div className="mock-stat-card" key={stat.label}>
                      <div className="stat-icon">{stat.icon}</div>
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  ))}
                </div>

                <div className="mock-messages">
                  {current.mock.messages.map((message) => (
                    <div
                      className={`message-bubble ${message.type}`}
                      key={`${message.type}-${message.text}`}
                    >
                      {message.text}
                    </div>
                  ))}
                </div>

                <div className="mock-composer">
                  <span className="composer-icon">✏️</span>
                  {current.mock.composer}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="steps-section">
        <div className="steps-section-shell">
          <div className="section-heading">
            <span className="eyebrow">{current.eyebrow}</span>
            <h2>{current.stepsTitle}</h2>
            <p>{current.stepsText}</p>
          </div>

          <div className="steps-grid">
            {current.steps.map((step) => (
              <article className="step-card" key={step.title}>
                <span className="step-number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="feature-section">
        <div className="feature-section-shell">
          <div className="section-heading">
            <span className="eyebrow">{current.featureEyebrow}</span>
            <h2>{current.featureTitle}</h2>
            <p>{current.featureText}</p>
          </div>

          <div className="feature-grid">
            {current.features.map((feature) => (
              <article className="feature-card" key={feature.title}>
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="faq-section-shell">
          <div className="section-heading">
            <span className="eyebrow">FAQ</span>
            <h2>Common questions about Talkali</h2>
            <p>
              Learn how Talkali handles creator messaging, paid replies, fan protection, and direct monetization.
            </p>
          </div>

          <div className="faq-grid">
            {faqItems.map((item) => (
              <article className="faq-card" key={item.question}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="download-section" id="apps-section">
        <div className="download-wrap">
          <div className="section-heading centered">
            <span className="eyebrow">Apps</span>
            <h2>Get started with Talkali</h2>
            <p>
              Open the iPhone app, or use Talkali on the web today.
            </p>
          </div>

          <div className="qr-grid">
            <article className="qr-card">
              <span className="qr-label">iPhone app</span>

              <div className="qr-box">
                <img
                  className="qr-image"
                  src={getQrUrl(APPLE_APP_URL)}
                  alt="Apple App Store QR code"
                />
              </div>

              <a
                className="secondary-button full-width"
                href={APPLE_APP_URL}
                target="_blank"
                rel="noreferrer"
              >
                Open iPhone app
              </a>
            </article>

            <article className="qr-card">
              <span className="qr-label">Android app</span>

              <div className="qr-box">
                <strong>Coming soon</strong>
              </div>

              <div className="secondary-button full-width">Coming soon on Android</div>
            </article>
          </div>

          <div className="download-actions">
            <a className="primary-button" href={WEB_APP_URL}>
              Open Talkali Web App
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default HomePage;